import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import {
  Button,
  SalesToolsWarning,
  SvgIcon,
  SvgNames,
  TextField,
  Typography,
  tokens,
  Switch,
  useSingletonMobileMediaQuery,
  Select,
  PillSwitchButtons,
} from '@sunrun/experience-ui-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isSunrunManaged } from '@sunrun/sales-experience-shared';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import { useSearchParams } from 'react-router-dom';
import { PipelineFilterMenu } from '../molecules/PipelineFilterMenu';
import { ListViewFilterMenu } from '../molecules/ListViewFilterMenu';
import { useUserProfile } from 'providers/UserProfile';
import { ListViewControls, PipelineControls } from 'types/prospect';
import { DEFAULT_LV_CONTROLS } from 'components/molecules/ListViewFilterMenu';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { shouldHideSalesToolsWarning } from 'helpers/listViewHelpers';
import {
  LIST_VIEW_SORT_OPTIONS,
  parseSortValue,
  toSortValue,
} from 'constants/ListViewSortConstants';
import { useListViewSortBreakpoints } from 'hooks/useListViewSortBreakpoints';
import { publish } from 'helpers/messaging';
import { WarningIconPopover } from 'components/atoms/WarningIconPopover';
import { SalePhase } from 'components/atoms/PipelineConsts';

const DEFAULT_PIPELINE_CONTROLS = {
  searchTerm: '',
  sort: 'default',
  salesReps: [],
  salesRepTeamScope: 'directReports',
  hierarchyDisplayMode: 'teams',
  includeSalesRepAction: false,
  includeUrgent: false,
  includedStarred: false,
  includeUnreadMessages: false,
  showCommission: true,
} as PipelineControls;

type PipelineHeaderProps = {
  appliedPipelineControls?: PipelineControls;
  appliedListViewControls?: ListViewControls;
  onApplyPipelineControl: React.Dispatch<
    React.SetStateAction<PipelineControls>
  >;
  onApplyListViewControl: React.Dispatch<
    React.SetStateAction<ListViewControls>
  >;
  onViewChange: (view: 'pipeline' | 'list') => void;
  view: 'pipeline' | 'list';
  isListViewRoute: boolean;
  showSearchListView: boolean;
  setShowSearchListView: (show: boolean) => void;
  isListViewDataLoading?: boolean;
  isListViewDataTruncated?: boolean;
  salePhase?: SalePhase;
  onSalePhaseChange?: (salePhase: SalePhase) => void;
};

const SALE_PHASE_OPTIONS: { label: string; value: SalePhase }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pre-sale', value: 'pre-sale' },
  { label: 'Post-sale', value: 'post-sale' },
];

const LIST_VIEW_TRUNCATION_TOOLTIP =
  'There may be more prospects in this date range than we can load. Narrow your "Creation date" filter to see additional results.';

const VIEW_TOGGLE_OPTIONS = [
  { icon: SvgNames.Accounts, value: 'pipeline' as const },
  { icon: SvgNames.List, value: 'list' as const },
];

type PipelineViewToggleProps = {
  view: 'pipeline' | 'list';
  onViewChange: (view: 'pipeline' | 'list') => void;
};

const PipelineViewToggle = ({
  view,
  onViewChange,
}: PipelineViewToggleProps) => (
  <ViewToggleContainer>
    {VIEW_TOGGLE_OPTIONS.map((option, index) => {
      const selected = option.value === view;
      const testId = `pipeline-view-toggle-${index}`;
      return (
        <ViewToggleButton
          key={option.value}
          data-testid={testId}
          type="button"
          selected={selected}
          onClick={() => onViewChange(option.value)}>
          <SvgIcon
            width={24}
            height={24}
            name={option.icon}
            color={selected ? 'white' : undefined}
          />
        </ViewToggleButton>
      );
    })}
  </ViewToggleContainer>
);

const PipelineHeader = ({
  appliedPipelineControls,
  appliedListViewControls,
  onApplyPipelineControl,
  onApplyListViewControl,
  onViewChange,
  view,
  isListViewRoute,
  showSearchListView,
  setShowSearchListView,
  isListViewDataLoading = false,
  isListViewDataTruncated = false,
  salePhase = 'all',
  onSalePhaseChange,
}: PipelineHeaderProps) => {
  const [searchParams] = useSearchParams();
  const filterUrgent = searchParams.get('filterUrgent');
  const { userProfile, splatUserType } = useUserProfile();
  const [showSearch, setShowSearch] = useState(false);
  const [pipelineControls, setPipelineControls] = useState<PipelineControls>({
    ...DEFAULT_PIPELINE_CONTROLS,
    includeUrgent: filterUrgent === 'true',
  });
  const [listViewControls, setListViewControls] = useState<ListViewControls>({
    ...DEFAULT_LV_CONTROLS,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const timeout = useRef<number>();
  const isMobile = useSingletonMobileMediaQuery();
  const { isMobile: isMobileSort, isTablet: isTabletSort } =
    useListViewSortBreakpoints();
  const isMobileOrTabletSort = isMobileSort || isTabletSort;
  const newListViewOnPipelineEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.NEW_LIST_VIEW_ON_PIPELINE,
  );

  const pipelineV2LayoutEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.PIPELINE_V2_LAYOUT,
  );

  const disableSearchFilters = !newListViewOnPipelineEnabled && view === 'list';
  const isCxPro = splatUserType === 'CX Pro';
  const isSunrunManagedUser = userProfile
    ? isSunrunManaged(userProfile)
    : false;

  useEffect(() => {
    if (appliedListViewControls) {
      setListViewControls(appliedListViewControls);
    }
  }, [appliedListViewControls]);

  useEffect(() => {
    setPipelineControls((current) => ({
      ...current,
      includeUrgent: filterUrgent === 'true',
    }));
  }, [filterUrgent, setPipelineControls]);

  const numberAppliedFilters = useMemo(() => {
    let count = 0;
    if (view === 'pipeline') {
      if (
        appliedPipelineControls?.salesReps &&
        appliedPipelineControls.salesReps.length > 0
      ) {
        count += 1;
      }
      if (appliedPipelineControls?.includeSalesRepAction) {
        count += 1;
      }
      if (appliedPipelineControls?.includeUrgent) {
        count += 1;
      }
      if (appliedPipelineControls?.includedStarred) {
        count += 1;
      }
      if (appliedPipelineControls?.includeUnreadMessages) {
        count += 1;
      }
    }
    if (view === 'list') {
      if (appliedListViewControls?.stage !== 'default') {
        count += 1;
      }
      if (
        appliedListViewControls?.beginCreatedDate ||
        appliedListViewControls?.endCreatedDate
      ) {
        count += 1;
      }
    }
    return count;
  }, [appliedPipelineControls, appliedListViewControls, view]);

  const searchChanged = useCallback(
    (newSearch: string) => {
      const lowercaseSearch = newSearch.toLowerCase();
      setPipelineControls((current) => ({
        ...current,
        searchTerm: lowercaseSearch,
      }));
      if (timeout.current) {
        window.clearTimeout(timeout.current);
      }
      timeout.current = window.setTimeout(() => {
        onApplyPipelineControl((current) => ({
          ...current,
          searchTerm: lowercaseSearch,
        }));
      }, 500);
    },
    [onApplyPipelineControl],
  );
  const searchChangedListView = useCallback(
    (newSearch: string) => {
      const lowercaseSearch = newSearch.toLowerCase();
      setListViewControls((current) => ({
        ...current,
        searchTerm: lowercaseSearch,
      }));
      if (timeout.current) {
        window.clearTimeout(timeout.current);
      }
      timeout.current = window.setTimeout(() => {
        onApplyListViewControl((current) => ({
          ...current,
          searchTerm: lowercaseSearch,
        }));
      }, 500);
    },
    [onApplyListViewControl],
  );

  const hideSearch = useCallback(() => {
    setShowSearch(false);
  }, [setShowSearch]);

  const hideSearchListView = useCallback(() => {
    setShowSearchListView(false);
  }, [setShowSearchListView]);

  useEffect(() => {
    if (showSearch && pipelineControls.searchTerm.length === 0) {
      window.addEventListener('click', hideSearch, { capture: true });
    }
    return () => {
      window.removeEventListener('click', hideSearch, { capture: true });
    };
  }, [showSearch, pipelineControls.searchTerm, hideSearch]);

  useEffect(() => {
    if (showSearchListView && listViewControls.searchTerm.length === 0) {
      window.addEventListener('click', hideSearchListView, { capture: true });
    }
    return () => {
      window.removeEventListener('click', hideSearchListView, {
        capture: true,
      });
    };
  }, [showSearchListView, listViewControls.searchTerm, hideSearchListView]);

  const title = useMemo(() => {
    if (isMobile && showSearch && view === 'pipeline') {
      return '';
    }
    if (isMobileSort && showSearchListView && view === 'list') {
      return '';
    }
    return view === 'pipeline' ? 'Pipeline' : 'List View';
  }, [isMobileSort, showSearch, showSearchListView, view, isMobile]);

  const listViewSortValue =
    view === 'list'
      ? toSortValue(
          appliedListViewControls?.sortParam ?? '',
          appliedListViewControls?.sortDirection ?? 'DESC',
        )
      : '';

  const handleListSortChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { param, direction } = parseSortValue(event.target.value);
      setListViewControls((prev) => ({
        ...prev,
        sortParam: param,
        sortDirection: direction,
      }));
      onApplyListViewControl((prev) => ({
        ...prev,
        sortParam: param,
        sortDirection: direction,
      }));
    },
    [onApplyListViewControl],
  );

  const listViewTruncationWarning =
    view === 'list' && isListViewDataTruncated ? (
      <WarningIconPopover
        contentTestId="list-view-truncation-warning-content"
        contentAriaLabel={LIST_VIEW_TRUNCATION_TOOLTIP}
        triggerAriaLabel="List view results limited"
        content={
          <Typography size="small">{LIST_VIEW_TRUNCATION_TOOLTIP}</Typography>
        }
      />
    ) : null;

  const salePhaseSwitch =
    pipelineV2LayoutEnabled && view === 'pipeline' ? (
      <StyledPillSwitchButtons
        isMobile={isMobile}
        fullWidth={isMobile}
        options={SALE_PHASE_OPTIONS}
        value={salePhase}
        onChange={(value) => onSalePhaseChange?.(value as SalePhase)}
        size="sm"
        aria-label="Filter by sale phase"
        testId="pipeline-sale-phase-switch"
      />
    ) : null;

  return (
    <PipelineHeaderContainer
      showBorder={isMobileOrTabletSort && view === 'list'}
      isMobile={isMobile}>
      <PipelineHeaderContent>
        <SalesToolsWarning
          sellsSunrun={shouldHideSalesToolsWarning(
            userProfile,
            splatUserType ?? '',
          )}
        />
        <PipelineHeaderContentContainer>
          <HeaderLeft>
            {title !== '' && (
              <Typography component="h1" size={tokens.FONT_SIZE_5}>
                {title}
              </Typography>
            )}
            {!isMobile && salePhaseSwitch}
            {!isMobile && !isCxPro && (
              <CreateNewLeadButton
                color="primary"
                size="xs"
                iconLeft={
                  <SvgIcon
                    name={SvgNames.Plus}
                    width={20}
                    height={20}></SvgIcon>
                }
                data-testid="create-new-lead-button"
                onClick={() => publish.createLead()}>
                <Typography
                  size={tokens.FONT_SIZE_1}
                  fontWeight="medium"
                  color="white">
                  Create lead
                </Typography>
              </CreateNewLeadButton>
            )}
          </HeaderLeft>
          <RightArea>
            {listViewTruncationWarning}
            {!showSearch && view === 'pipeline' && (
              <SearchButton
                color="tertiary"
                aria-label="Search"
                iconMiddle={
                  <SvgIcon
                    width={24}
                    height={24}
                    name={SvgNames.Search}></SvgIcon>
                }
                onClick={() => setShowSearch(true)}
              />
            )}
            {!showSearchListView &&
              view === 'list' &&
              !disableSearchFilters && (
                <ListViewSearchWrapper>
                  {isListViewDataLoading && (
                    <ListViewLoadingSpinner
                      name={SvgNames.Loading}
                      width={24}
                      height={24}
                      color={tokens.BRAND_HERO_BLUE}
                    />
                  )}
                  <SearchButton
                    color="tertiary"
                    aria-label="Search"
                    iconMiddle={<SvgIcon name={SvgNames.Search}></SvgIcon>}
                    onClick={() => setShowSearchListView(true)}
                  />
                </ListViewSearchWrapper>
              )}
            {showSearch && view === 'pipeline' && (
              <TextField
                style={{ height: '36px' }}
                autoFocus
                label=""
                placeholder="Search"
                data-testid="pipeline-search-input"
                value={pipelineControls.searchTerm}
                onChange={(event) => searchChanged(event.target.value)}
              />
            )}
            {showSearchListView && view === 'list' && !disableSearchFilters && (
              <ListViewSearchWrapper>
                {isListViewDataLoading && (
                  <ListViewLoadingSpinner
                    name={SvgNames.Loading}
                    width={24}
                    height={24}
                    color={tokens.BRAND_HERO_BLUE}
                  />
                )}
                <TextField
                  style={{ height: '36px' }}
                  autoFocus
                  label=""
                  placeholder="Search"
                  data-testid="listview-search-input"
                  value={listViewControls.searchTerm}
                  onChange={(event) =>
                    searchChangedListView(event.target.value)
                  }
                />
              </ListViewSearchWrapper>
            )}
            {view === 'list' && isTabletSort && (
              <StyledSelect
                selectType="button"
                buttonProps={{
                  size: 'sm',
                  color: 'secondary',
                }}
                options={LIST_VIEW_SORT_OPTIONS}
                value={listViewSortValue}
                onChange={handleListSortChange}
              />
            )}
            {view === 'pipeline' && !isListViewRoute && isSunrunManagedUser && (
              <Switch
                size="xlarge"
                checked={pipelineControls.showCommission}
                testId="pipeline-commission-switch"
                onClick={() => {
                  const showCommission = !pipelineControls.showCommission;
                  setPipelineControls((current) => ({
                    ...current,
                    showCommission,
                  }));
                  onApplyPipelineControl((current) => ({
                    ...current,
                    showCommission,
                  }));
                }}
                onChange={() => {}}
                icon={
                  <SvgIcon
                    name={SvgNames.DollarSign}
                    color={tokens.BRAND_HERO_BLUE}
                  />
                }
              />
            )}
            {!disableSearchFilters && (
              <FiltersButton
                color="secondary"
                size="xs"
                aria-label="Filters"
                iconLeft={
                  <SvgIcon
                    name={SvgNames.Filters}
                    width={20}
                    height={20}></SvgIcon>
                }
                iconRight={
                  numberAppliedFilters > 0 && (
                    <AppliedFilterCount>
                      {numberAppliedFilters}
                    </AppliedFilterCount>
                  )
                }
                onClick={() => {
                  if (appliedPipelineControls) {
                    setPipelineControls(appliedPipelineControls);
                  }
                  if (appliedListViewControls) {
                    setListViewControls(appliedListViewControls);
                  }
                  setFiltersOpen(true);
                }}>
                {isMobile ? '' : 'Filters'}
              </FiltersButton>
            )}
            {!isListViewRoute && !isCxPro && (
              <PipelineViewToggle view={view} onViewChange={onViewChange} />
            )}
          </RightArea>
        </PipelineHeaderContentContainer>
        {isMobile && salePhaseSwitch}
      </PipelineHeaderContent>
      {view === 'pipeline' && (
        <PipelineFilterMenu
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          controls={pipelineControls}
          setControls={setPipelineControls}
          onApplyControl={onApplyPipelineControl}
        />
      )}
      {view === 'list' && (
        <ListViewFilterMenu
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          listViewControls={listViewControls}
          setListViewControls={setListViewControls}
          onApplyListViewControl={onApplyListViewControl}
        />
      )}
      {isMobile && !isCxPro && (
        <FloatingCreateNewLeadButton
          color="primary"
          size="sm"
          aria-label="Create lead"
          data-testid="create-new-lead-button"
          onClick={() => publish.createLead()}>
          <SvgIcon name={SvgNames.Plus} width={24} height={24}></SvgIcon>
        </FloatingCreateNewLeadButton>
      )}
    </PipelineHeaderContainer>
  );
};

const StyledSelect = styled(Select)`
  .sr-button-sm.sr-button-secondary {
    padding-top: 5px;
    padding-bottom: 5px;
  }

  > svg {
    margin: 0;
  }
`;

const StyledPillSwitchButtons = styled(PillSwitchButtons)<{
  isMobile?: boolean;
}>`
  height: 34px !important;
  > button {
    font-size: ${tokens.FONT_SIZE_1}px;
    min-width: 60px;
  }
  ${({ isMobile }) => isMobile && 'margin-top: 8px'};
`;

const PipelineHeaderContainer = styled.div<{
  showBorder: boolean;
  isMobile: boolean;
}>`
  padding: ${({ isMobile }) => (isMobile ? '16px' : '1.5rem')};
  display: flex;
  // This is to ensure the Sort Dropdown Button is above the list view in tablet view
  z-index: 1;
  flex-direction: row;
  border-bottom: ${(props) =>
    props.showBorder ? `1px solid ${tokens.OFFWHITE_40}` : 'none'};
`;

const PipelineHeaderContent = styled.div`
  position: sticky;
  left: 1.5rem;
  width: calc(100vw - 4rem);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PipelineHeaderContentContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const CreateNewLeadButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
`;

const FloatingCreateNewLeadButton = styled(Button)`
  position: fixed;
  padding: 0.5rem;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 100px;

  svg {
    display: block;
  }
`;

const RightArea = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  z-index: 1;
`;

const FiltersButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 12px !important;

  > span {
    font-weight: 500;
  }
`;

const AppliedFilterCount = styled.div`
  background-color: ${tokens.BRAND_HERO_BLUE};
  color: ${tokens.WHITE};
  font-size: ${tokens.FONT_SIZE_0}px;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border-radius: 50%;
  margin-left: 8px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
  padding-bottom: 1px;
  margin-top: 1px;
`;

const SearchButton = styled(Button)`
  padding: 0;
`;

const ListViewSearchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const ListViewLoadingSpinner = styled(SvgIcon)`
  flex-shrink: 0;
`;

const ViewToggleContainer = styled.div`
  border: 1px solid ${tokens.BRAND_HERO_BLUE};
  border-radius: 4px;
  padding: 2px;
  display: flex;
`;

const ViewToggleButton = styled.button<{ selected: boolean }>`
  border-radius: 3px;
  padding: 2px;
  background: unset;
  border: none;
  cursor: pointer;
  display: flex;

  ${(props) =>
    props.selected &&
    css`
      background: ${tokens.BRAND_MEDIUM_BLUE};
    `}
`;

export type { PipelineControls };
export { DEFAULT_PIPELINE_CONTROLS, DEFAULT_LV_CONTROLS, PipelineHeader };
